import React, { ChangeEvent, DragEvent, MouseEventHandler, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@mui/material';

import { ReactComponent as FrameIcon } from "@/assets/icons/Frame.svg";
import { FileDetails, Image } from '@/types/Image.type';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { generateUniqueId, getFileExtension, interpolate } from '@/utils/string.util';
import { isImageSupported, supportedImageTypes } from '@/utils/image.util';
import { toast } from 'react-toastify';
import { ImageCard } from '../ImageCard/ImageCard';

import styles from './Images2.module.scss';
import Select from 'react-select';
import { ImageType } from '@/types/Template.type.ts';

interface ImageProps2 {
	type: InspectionImageType
	images: Image[],
	inspectionId: string,
	onAdd: (file: FileDetails) => void,
	onRemove: (img: Image) => void
	setSelectedLabelType?: React.Dispatch<React.SetStateAction<string>>;
	selectedLabelType?: string
	imageTypes: ImageType[] | undefined;
}

export const Images2: React.FC<ImageProps2> = ({
	type,
	images,
	onAdd, onRemove,
	inspectionId,
	setSelectedLabelType,
	selectedLabelType,
	imageTypes
}: ImageProps2) => {
	const [isDragging, setIsDragging] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { t } = useTranslation();
	const DEFAULT_IMAGE_TYPE = 'Full Product Image';
	const imageTypeOptions = useMemo(() => {
		if (!imageTypes || !Array.isArray(imageTypes)) return [];
		return imageTypes
			.sort((a, b) => a.order - b.order)
			.map(type => ({
				value: type.name,
				label: type.name
			}));
	}, [imageTypes]);

	const { t: translate } = useTranslation()



	const handleAddImage = async (
		event: ChangeEvent<HTMLInputElement> | DragEvent<HTMLDivElement>
	) => {
		let files: FileList | null = null;

		if (event.type === 'change') {
			files = (event as ChangeEvent<HTMLInputElement>).target.files;
		} else if (event.type === 'drop') {
			event.preventDefault();
			files = (event as DragEvent<HTMLDivElement>).dataTransfer.files;
			setIsDragging(false);
		}

		if (files && files[0]) {
			for (const file of Array.from(files)) {
				if (!isImageSupported(file)) {
					toast.error(interpolate(t('supported_images'), [supportedImageTypes.join(', ').replaceAll('image/', '')]));
					continue;
				}
				const reader = new FileReader();
				reader.onloadend = async () => {
					const fileData = (reader.result as string);
					const uniqueId = generateUniqueId();
					const img: Image = {
						type: type,
						id: uniqueId,
						name: `${uniqueId}${getFileExtension(file)}`,
						imageType: selectedLabelType,
						data: fileData,
						contentType: file.type
					};
					onAdd(img);
				};
				reader.readAsDataURL(file);
			}
		}
	};


	const handleDeleteImage = async (img: Image) => {
		onRemove(img);
	};

	const triggerFileInput = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);
	};

	const groupedImages = useMemo(() => {
		return images.reduce((groups: Record<string, Image[]>, img) => {
			let typeName: string;
			if (typeof img.imageType === 'object' && img.imageType !== null) {
				typeName = img.imageType;
			} else if (typeof img.imageType === 'string') {
				typeName = img.imageType;
			} else if (img.url) {
				const urlObj = new URL(img.url);
				typeName = urlObj.searchParams.get("imageType") || DEFAULT_IMAGE_TYPE;
			} else {
				typeName = DEFAULT_IMAGE_TYPE;
			}

			if (!groups[typeName]) groups[typeName] = [];
			groups[typeName].push(img);

			return groups;
		}, {});
	}, [images]);

	const allInspectionImageGroups = useMemo(
		() =>
			Object.entries(groupedImages).map(([typeLabel, imgs]) => ({
				typeLabel,
				inspectionImageType: type,
				images: imgs,
			})),
		[groupedImages, type],
	);

	/** One carousel for the whole module: sections top-to-bottom, then images left-to-right within each section */
	const allImagesCarouselOrder = useMemo(
		() => Object.entries(groupedImages).flatMap(([, imgs]) => imgs),
		[groupedImages],
	);


	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		handleAddImage(event);
	};
	function addImageButton(listener: MouseEventHandler<HTMLDivElement>) {
		return (
			<div
				className={styles.buttonContainer}
			>
				<div
					className={images.length ? styles.addBtn : styles.cameraBtn} // Conditional class assignment
					onClick={listener}
				>
					{images.length ? (
						<p>{t('Add') + ' +'}</p>
					) : (
						<SvgIcon
							component={FrameIcon}
							fontSize="large"
							sx={{
								width: 100,
								height: 45,
								color: "white",
							}}
							viewBox="0 0 50 50"
						/>
					)}
				</div>
			</div>
		);
	}



	return (
		<div
			className={styles.container}
		>
			<div>
				<div className={styles.heading}>{t('Images')}</div>
			</div>

			<hr className={styles.hr} />

			<div className={styles.dropdownAddBtnContainer} >
				<div className={styles.dropdownContainer}>
					<label className={styles.label} htmlFor="defectCode">
						{translate('Image Type')}
					</label>

					<Select
						id="imageType"
						name="imageType"
						options={imageTypeOptions}
						value={imageTypeOptions.find(opt => opt.value === selectedLabelType) || imageTypeOptions[0] || null}
						onChange={(option) => {
							if (setSelectedLabelType && option?.value) {
								setSelectedLabelType(option.value);
							}
						}}
						placeholder="Select or Search..."
						isSearchable
						styles={{
							control: (provided) => ({
								...provided,
								fontSize: '.85rem',
								color: '#000000B3',
								borderColor: '#A7A7A7',
								boxShadow: 'none',
								'&:hover': { borderColor: '#A7A7A7' },
							}),
							option: (provided, state) => ({
								...provided,
								fontSize: '.85rem',
								backgroundColor: state.isSelected
									? '#0C63E4'
									: state.isFocused
										? '#0C63E4'
										: 'white',
								color: state.isSelected || state.isFocused ? 'white' : '#000000B3',
							}),
						}}
					/>
				</div>

				{addImageButton(triggerFileInput)}
			</div>




			<div
				className={`${styles.dropArea} ${isDragging ? styles.dragging : ''}`}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				{!!images.length && (
					<div className={styles.imageSections}>
						{Object.entries(groupedImages).map(([label, imgs]) => (
							<div key={label} className={styles.section}>
								<div className={styles.labelContainer} >
									<p className={styles.sectionTitle}>{label}</p>
								</div>

								<div className={styles.imageGrid}>
									{imgs.map((image) => (
										<div key={image.id} className={styles.imageContainer}>
											<ImageCard
												type={type}
												inspectionId={inspectionId}
												image={image}
												onDelete={handleDeleteImage}
												galleryPeers={allImagesCarouselOrder}
												galleryIndex={allImagesCarouselOrder.findIndex((i) => i.id === image.id)}
												sectionTypeLabel={label}
												allInspectionImageGroups={allInspectionImageGroups}
											/>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				)}

				<input
					hidden={true}
					type="file"
					// do not remove ["image/*", "text/plain"] its critical for taking image from camera
					accept={[...supportedImageTypes, "image/*", "text/plain"].join(", ")}
					onChange={handleAddImage}
					className={styles.uploadInput}
					ref={fileInputRef}
				/>
				<p className={styles.supportedFormats}>{interpolate(t('supported_images'), [supportedImageTypes.join(', ').replaceAll('image/', '')])}</p>
			</div>
		</div >
	)
		;
};
